import { LicenceModel } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import type { CheckoutPlan } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { userType, packageLicenceIds, newPricingPackageLicenceIds, routes } from '../../constants';

/**
 * SM-202. Builds the URL for the basis-user resubscribe Stripe Elements
 * page: `/upgrade-options/bestaetigen/checkout?targetPlanKey=<plan>`.
 * Centralised so `StripeUpgradePage.handleSelectPlan` (routing basis users
 * around the confirm page) and `StripeUpgradeConfirmPage` (mount-time
 * safety redirect for basis users landing there via back button / paste)
 * can't drift apart when the route constants get reshuffled.
 */
export const getStripeUpgradeCheckoutUrl = (targetPlanKey: CheckoutPlan): string => {
  const { stripeUpgrade, stripeUpgradeConfirm, stripeUpgradeCheckout } = routes;
  const base = `/${stripeUpgrade.root}/${stripeUpgradeConfirm.root}/${stripeUpgradeCheckout.root}`;
  return `${base}?targetPlanKey=${targetPlanKey}`;
};

const {
  meinUnterrichtPro,
  meineFortbildung,
  meinUnterricht360,
  meinUnterricht360ForDocumentUsers
} = packageLicenceIds;
const {
  meinUnterrichtProKI,
  meinUnterricht360KI,
  meinUnterrichtProTrial
} = newPricingPackageLicenceIds;

const OLD_PRICING_PLANS = {
  [userType.document]: [meinUnterrichtPro],
  [userType.courses]: [meineFortbildung],
  [userType.kombi]: [meinUnterricht360, meinUnterricht360ForDocumentUsers],
};

const NEW_PRICING_PLANS = {
  [userType.document]: [meinUnterrichtProKI],
  [userType.courses]: [meineFortbildung],
  [userType.kombi]: [meinUnterricht360KI],
};

const OLD_PRICING_PANS_ORDER = {
  [userType.basis]: [meineFortbildung, meinUnterrichtPro, meinUnterricht360],
  [userType.document]: [meinUnterrichtPro, meinUnterricht360ForDocumentUsers],
  [userType.courses]: [meineFortbildung, meinUnterricht360],
  [userType.kombi]: [meinUnterricht360]
};

const NEW_PRICING_PLANS_ORDER = {
  [userType.basis]: [meineFortbildung, meinUnterrichtProKI, meinUnterricht360KI],
  [userType.document]: [meinUnterrichtProKI, meinUnterricht360KI],
  [userType.courses]: [meineFortbildung, meinUnterricht360KI],
  [userType.kombi]: [meinUnterricht360KI]
};

// Upgradeable plans for new pricing logic
const UPGRADEABLE_PLANS = new Set([meinUnterrichtPro, meinUnterrichtProKI, meinUnterrichtProTrial]);

/**
 * Optimized function to check if a licence is the user's current plan
 */
export const isLicenceIdCurrentUserPlan = (type: number, licenceId: number): boolean => {
  const userPlans = OLD_PRICING_PLANS[type];
  return userPlans?.includes(licenceId) ?? false;
};

/**
 * Optimized function to order plans based on user type
 * Uses a more efficient sorting algorithm with early returns
 */
export const orderOldPlans = (plans: LicenceModel[], type: number | undefined): LicenceModel[] => {
  if (!type || !plans.length) return plans;
  
  const userTypeOrder = OLD_PRICING_PANS_ORDER[type];
  if (!userTypeOrder) return plans;
  
  // Create a Map for O(1) lookup instead of O(n) indexOf
  const orderMap = new Map(userTypeOrder.map((id, index) => [id, index]));
  
  return plans.sort((a, b) => {
    const indexA = orderMap.get(a.licenceModelId) ?? Number.MAX_SAFE_INTEGER;
    const indexB = orderMap.get(b.licenceModelId) ?? Number.MAX_SAFE_INTEGER;
    
    return indexA - indexB;
  });
};

/**
 * Optimized function to check if a licence is the user's current plan for new pricing logic
 */
export const isNewPricingCurrentUserPlan = (
  type: number, 
  licenceId: number, 
  userLicenceId?: number
): boolean => {
  // Direct comparison is fastest
  if (userLicenceId && userLicenceId === licenceId) {
    return true;
  }

  const userPlans = NEW_PRICING_PLANS[type];
  return userPlans?.includes(licenceId) ?? false;
};

/**
 * Optimized function to order plans for new pricing logic
 * Handles upgrade scenarios efficiently
 */
export const orderNewPricingPlans = (
  plans: LicenceModel[], 
  type: number | undefined, 
  userLicenceId?: number
): LicenceModel[] => {
  if (!plans.length) return plans;
  
  // Handle upgrade scenario efficiently
  if (userLicenceId && UPGRADEABLE_PLANS.has(userLicenceId)) {
    const currentPlan = plans.find(plan => plan.licenceModelId === userLicenceId);
    const upgradePlan = plans.find(plan => plan.licenceModelId === meinUnterricht360KI);
    
    if (currentPlan && upgradePlan) {
      return [currentPlan, upgradePlan];
    }
  }
  
  // Default ordering for new pricing plans
  if (!type) return plans;
  
  const userTypeOrder = NEW_PRICING_PLANS_ORDER[type];
  if (!userTypeOrder) return plans;
  
  const orderMap = new Map(userTypeOrder.map((id, index) => [id, index]));
  
  return plans.sort((a, b) => {
    const indexA = orderMap.get(a.licenceModelId) ?? Number.MAX_SAFE_INTEGER;
    const indexB = orderMap.get(b.licenceModelId) ?? Number.MAX_SAFE_INTEGER;
    
    return indexA - indexB;
  });
};
